import { ReactNode } from 'react';
import { Avatar } from 'antd';
import {
    TbAdjustmentsCog,
    TbBuilding,
    TbKeyboard,
    TbLayoutGrid,
    TbLifebuoy,
    TbPointerBolt,
    TbUser,
    TbUsers,
} from 'react-icons/tb';

import { BoxSVGIcon, DropboxSVGIcon, GoogleDriveSVGIcon, NotionSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useMaps } from '@/hooks/useMaps';
import { WorkspaceResourceAvatar, WorkspaceResourceGlyph } from '@/shared/components/workspace-resources';
import { imgLogoStyling } from '@/theme';
import { corporateLogo } from '@/utilities';

import {
    COMMAND_PALETTE_DATA_SOURCE_GROUPS,
    commandPaletteDataSourceGroupOrder,
    isCommandPaletteDataSourceGroup,
} from '../constants/dataSources';

interface CommandPaletteResourceItem {
    enrichment?: {
        website?: string;
    };
    mimeType?: string;
    website?: string;
    [key: string]: unknown;
}

export const useCommandPaletteIcons = () => {
    const { token } = useAntDesignTheme();
    const resourceGroupIcon = (resource: string) => (
        <WorkspaceResourceGlyph
            resource={resource}
            style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
        />
    );
    const dataSourceAvatarFor = (group: string, marginRight = '0.25rem') => {
        const dataSource = COMMAND_PALETTE_DATA_SOURCE_GROUPS[group as keyof typeof COMMAND_PALETTE_DATA_SOURCE_GROUPS];

        if (!dataSource) {
            return undefined;
        }

        return (
            <Avatar
                size={16}
                style={{
                    verticalAlign: '-0.175rem',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginRight,
                }}
                shape='square'
                icon={
                    <img
                        alt={dataSource.label}
                        src={dataSource.icon}
                        style={{
                            ...imgLogoStyling,
                            transform: 'scale(0.75)',
                        }}
                    />
                }
            />
        );
    };

    const groupToIconMap = {
        resourceActions: (
            <TbPointerBolt
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        navigation: (
            <TbKeyboard
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        quickActions: (
            <TbPointerBolt
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        account: (
            <TbUser style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }} />
        ),
        workspace: (
            <TbLayoutGrid
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        help: (
            <TbLifebuoy
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        miscellaneous: (
            <TbAdjustmentsCog
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        // Queryable Sections
        folders: resourceGroupIcon('folder'),
        investments: resourceGroupIcon('investment'),
        lifecycles: resourceGroupIcon('lifecycle'),
        memos: resourceGroupIcon('memo'),
        pipelines: resourceGroupIcon('pipeline'),
        dashboards: resourceGroupIcon('dashboard'),
        lists: resourceGroupIcon('list'),
        tasks: resourceGroupIcon('task'),
        teams: resourceGroupIcon('team'),
        people: (
            <TbUsers style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }} />
        ),
        companies: (
            <TbBuilding
                style={{ verticalAlign: '-0.075rem', marginRight: '0.25rem', color: token.colorTextTertiary }}
            />
        ),
        googleDrive: (
            <Avatar
                size={16}
                style={{
                    verticalAlign: '-0.175rem',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginRight: '0.25rem',
                }}
                shape='square'
                icon={
                    <img
                        alt='Google Drive'
                        src={GoogleDriveSVGIcon}
                        style={{
                            ...imgLogoStyling,
                            transform: 'scale(0.75)',
                        }}
                    />
                }
            />
        ),
        box: (
            <Avatar
                size={16}
                style={{
                    verticalAlign: '-0.175rem',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginRight: '0.25rem',
                }}
                shape='square'
                icon={
                    <img
                        alt='Box'
                        src={BoxSVGIcon}
                        style={{
                            ...imgLogoStyling,
                            transform: 'scale(0.75)',
                        }}
                    />
                }
            />
        ),
        dropbox: (
            <Avatar
                size={16}
                style={{
                    verticalAlign: '-0.175rem',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginRight: '0.25rem',
                }}
                shape='square'
                icon={
                    <img
                        alt='Dropbox'
                        src={DropboxSVGIcon}
                        style={{
                            ...imgLogoStyling,
                            transform: 'scale(0.75)',
                        }}
                    />
                }
            />
        ),
        notion: (
            <Avatar
                size={16}
                style={{
                    verticalAlign: '-0.175rem',
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    marginRight: '0.25rem',
                }}
                shape='square'
                icon={
                    <img
                        alt='Notion'
                        src={NotionSVGIcon}
                        style={{
                            ...imgLogoStyling,
                            transform: 'scale(0.75)',
                        }}
                    />
                }
            />
        ),
        ...Object.fromEntries(commandPaletteDataSourceGroupOrder.map((group) => [group, dataSourceAvatarFor(group)])),
    } as Record<string, ReactNode>;

    const groupOrder = [
        'folders',
        'investments',
        'lifecycles',
        'memos',
        'pipelines',
        'dashboards',
        'lists',
        'teams',
        'people',
        ...commandPaletteDataSourceGroupOrder,
        'companies',
        'resourceActions',
        'navigation',
        'quickActions',
        'account',
        'workspace',
        'help',
        'miscellaneous',
    ];

    const { mimeTypeToIconMap } = useMaps();

    // Item Mappings

    const itemToMap = (resourceType: string, resourceItem: unknown, marginRight = '0.375rem') => {
        const item =
            resourceItem && typeof resourceItem === 'object' ? (resourceItem as CommandPaletteResourceItem) : {};
        const itemMimeType = typeof item?.mimeType === 'string' ? item.mimeType : undefined;
        const itemMimeTypeIcon = itemMimeType ? mimeTypeToIconMap[itemMimeType] : undefined;

        // let ext;

        // if (resourceType === 'box' || resourceType === 'dropbox') {
        //     ext = item.name.split('.').pop()?.toLowerCase();
        // }

        const resourceItemAvatar = (resource: string, website?: string, listType?: string) => (
            <WorkspaceResourceAvatar
                listType={listType}
                resource={resource}
                size={16}
                website={website}
                style={{ verticalAlign: '-0.125rem', marginRight }}
            />
        );
        const map = {
            folders: resourceItemAvatar('folder'),
            investments: resourceItemAvatar('investment', item?.website),
            lifecycles: resourceItemAvatar('lifecycle'),
            memos: resourceItemAvatar('memo'),
            pipelines: resourceItemAvatar('pipeline'),
            dashboards: resourceItemAvatar('dashboard'),
            lists: resourceItemAvatar('list', undefined, typeof item?.type === 'string' ? item.type : undefined),
            tasks: resourceItemAvatar('task'),
            teams: resourceItemAvatar('team'),
            people: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        <TbUser
                            size='0.675rem'
                            style={{ verticalAlign: '-0.0825rem', color: token.colorTextSecondary }}
                        />
                    }
                />
            ),
            // Others
            companies: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    src={item?.enrichment?.website && corporateLogo(item?.enrichment?.website)}
                    icon={
                        <TbBuilding
                            size='0.675rem'
                            style={{ verticalAlign: '-0.075rem', color: token.colorTextSecondary }}
                        />
                    }
                />
            ),
            googleDrive: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        itemMimeTypeIcon ? (
                            itemMimeTypeIcon
                        ) : (
                            <img
                                alt='Google Drive'
                                src={GoogleDriveSVGIcon}
                                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                            />
                        )
                    }
                />
            ),
            box: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        // fileExtensionToIconMap[ext] ? (
                        //     fileExtensionToIconMap[ext]
                        // ) : (
                        <img alt='Box' src={BoxSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
                        // )
                    }
                />
            ),
            dropbox: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        // fileExtensionToIconMap[ext] ? (
                        //     fileExtensionToIconMap[ext]
                        // ) : (
                        <img
                            alt='Dropbox'
                            src={DropboxSVGIcon}
                            style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                        />
                        // )
                    }
                />
            ),
            notion: (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        <img alt='Notion' src={NotionSVGIcon} style={{ ...imgLogoStyling, transform: 'scale(0.75)' }} />
                    }
                />
            ),
        } as Record<string, ReactNode>;

        if (map[resourceType]) {
            return map[resourceType];
        }

        if (isCommandPaletteDataSourceGroup(resourceType)) {
            const dataSource =
                COMMAND_PALETTE_DATA_SOURCE_GROUPS[resourceType as keyof typeof COMMAND_PALETTE_DATA_SOURCE_GROUPS];
            const googleWorkspaceIcon =
                resourceType === 'googleDrive' || resourceType === 'googleSheets' ? itemMimeTypeIcon : undefined;

            return (
                <Avatar
                    size={16}
                    style={{
                        verticalAlign: '-0.125rem',
                        backgroundColor: token.colorBgBase,
                        border: `1px solid ${token.colorBorder}`,
                        marginRight,
                    }}
                    shape='square'
                    icon={
                        googleWorkspaceIcon ?? (
                            <img
                                alt={dataSource.label}
                                src={dataSource.icon}
                                style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                            />
                        )
                    }
                />
            );
        }

        return undefined;
    };

    return { groupOrder, groupToIconMap, itemToMap };
};
